import {
    Box,
    Checkbox,
    FormControl,
    FormHelperText,
    IconButton,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    SelectChangeEvent,
    Typography,
} from '@mui/material'
import { ReactElement, useEffect, useState } from 'react'
import TruncatedText from './TruncatedText'
import { AddRounded, Clear } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

interface MultiSelectInputProps {
    id: string
    label: string
    value: string[]
    onChange: any
    list: any[]
    inputProps?: {
        id: string
        name: string
    }
    errorMessage?: string
    isLegend?: boolean
    required?: boolean
    disabled?: boolean
    ui_width?: string
    hasAddNew?: boolean
    onClickAddNew?: any
    onClickRowIcon?: any
    itemEditDisable?: string
    ariaLabel?: string
    title?: string
    allowClear?: boolean
    displayInputLabel?: boolean
    renderLabelInValue?: boolean
    displayEmpty?: boolean
}

const MultiSelectInput = (props: MultiSelectInputProps): ReactElement => {
    const {
        id,
        label,
        value,
        onChange,
        list,
        inputProps,
        errorMessage = '',
        isLegend = false,
        required,
        disabled,
        ui_width,
        hasAddNew,
        onClickAddNew,
        onClickRowIcon,
        itemEditDisable,
        title,
        allowClear = true,
        displayInputLabel = true,
        renderLabelInValue = false,
        displayEmpty = false,
    } = props
    const [open, setOpen] = useState(false)
    const { t } = useTranslation()

    const [selected, setSelected] = useState<string[]>([])

    useEffect(() => {
        setSelected(value)
    }, [value])

    const handleChange = (event: SelectChangeEvent<typeof selected>) => {
        const {
            target: { value },
        } = event
        onChange(id, typeof value === 'string' ? value.split(',') : value)
    }

    const handleClear = (e: any) => {
        e.stopPropagation()
        onChange(id, [])
    }

    const onClickRowIconMulti = (e: any, item: any) => {
        e.stopPropagation()
        setOpen(false)
        onClickRowIcon(item)
    }
    const labelId = `select-label-id-${id}`

    return (
        <Box sx={{ width: '100%' }}>
            <FormControl fullWidth disabled={!!disabled} size="small">
                {isLegend ? (
                    <>
                        {displayInputLabel && (
                            <InputLabel
                                id={labelId}
                                sx={{
                                    opacity: '0.7',
                                    fontSize: '14px',
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap',
                                    textOverflow: 'ellipsis',
                                    width: 'calc(100% - 40px)',
                                    '&[data-shrink="true"]': {
                                        width: 'calc(133% - 32px)',
                                    },
                                }}
                            >
                                {' '}
                                {
                                    <Box>
                                        <TruncatedText text={label} />
                                    </Box>
                                }
                                {required ? '*' : ''}
                            </InputLabel>
                        )}
                        <Select
                            labelId={labelId}
                            id={id}
                            value={selected}
                            size="small"
                            label={displayInputLabel ? label : ''}
                            title={title || label}
                            aria-label={props.ariaLabel}
                            multiple
                            displayEmpty={displayEmpty}
                            onChange={handleChange}
                            sx={{
                                width: '100%',
                                fontSize: '14px',
                                '&:hover .clear-icon': {
                                    opacity: 1,
                                },
                            }}
                            renderValue={(selected) => {
                                if (renderLabelInValue) {
                                    return <TruncatedText text={label} />
                                }
                                if (selected.length !== 0) {
                                    return (
                                        <Box width="100%">
                                            <TruncatedText
                                                sx={{ fontSize: '14px' }}
                                                text={selected
                                                    .map((v) => {
                                                        const item = list.find(
                                                            (i) =>
                                                                inputProps?.id
                                                                    ? i[
                                                                          inputProps
                                                                              .id
                                                                      ] === v
                                                                    : i.id ===
                                                                      v,
                                                        )

                                                        // Check if an item is found and retrieve the appropriate property
                                                        return item
                                                            ? inputProps?.name
                                                                ? item[
                                                                      inputProps
                                                                          .name
                                                                  ]
                                                                : item.name
                                                            : ''
                                                    })
                                                    .filter(Boolean) // Filter out empty strings if no item is found
                                                    .join(', ')}
                                            />
                                        </Box>
                                    )
                                }
                            }}
                            MenuProps={{
                                anchorOrigin: {
                                    vertical: 'bottom',
                                    horizontal: 'left',
                                },
                                transformOrigin: {
                                    vertical: 'top',
                                    horizontal: 'left',
                                },
                                PaperProps: {
                                    sx: {
                                        maxHeight: 250,
                                        width: ui_width ? ui_width : '',
                                    },
                                },
                            }}
                            error={errorMessage !== ''}
                            endAdornment={
                                allowClear &&
                                selected?.length > 0 &&
                                !disabled ? (
                                    <IconButton
                                        className="clear-icon"
                                        sx={{
                                            display: 'flex',
                                            position: 'absolute',
                                            right: '25px',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            opacity: 0,
                                            transition: 'opacity 0.2s ease',
                                        }}
                                        onClick={handleClear}
                                        size="small"
                                        id={`${id}-clear-button`}
                                    >
                                        <Clear
                                            sx={{
                                                margin: 'auto',
                                                fontSize: '1.25rem',
                                            }}
                                            id={`${id}-clear-icon`}
                                        />
                                    </IconButton>
                                ) : null
                            }
                        >
                            {hasAddNew && (
                                <MenuItem
                                    sx={{
                                        background: '#fff !important',
                                        paddingLeft: '8px',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            width: '100%',
                                        }}
                                        id={id + '-add-new'}
                                        onClick={() => onClickAddNew()}
                                    >
                                        <AddRounded color={'primary'} />
                                        <Box
                                            sx={{
                                                marginLeft: '3px',
                                            }}
                                        >
                                            <Typography
                                                fontWeight="600"
                                                fontSize="14px"
                                            >
                                                {t('addNew')}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </MenuItem>
                            )}
                            {list.map((item, index) => (
                                <MenuItem
                                    sx={{
                                        fontSize: '14px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        width: ui_width ? ui_width : 'auto',
                                        padding: '7px 15px 7px 10px',
                                        '&:hover': {
                                            '& > .row-icon': {
                                                display: 'flex',
                                            },
                                        },
                                    }}
                                    id={id + '-option-' + index}
                                    key={
                                        inputProps?.id
                                            ? item[inputProps.id]
                                            : item.id
                                    }
                                    value={
                                        inputProps?.id
                                            ? item[inputProps.id]
                                            : item.id
                                    }
                                >
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        width={
                                            onClickRowIcon
                                                ? 'calc(100% - 20px)'
                                                : '100%'
                                        }
                                    >
                                        <Checkbox
                                            id={
                                                id +
                                                '-option-' +
                                                index +
                                                '-checkbox'
                                            }
                                            size="small"
                                            checked={
                                                selected.indexOf(
                                                    inputProps?.id
                                                        ? item[inputProps.id]
                                                        : item.id,
                                                ) > -1
                                            }
                                            sx={{
                                                padding: '0px',
                                                paddingRight: '5px',
                                            }}
                                        />
                                        <TruncatedText
                                            text={
                                                inputProps?.name
                                                    ? item[inputProps.name]
                                                    : item.name
                                            }
                                        />
                                    </Box>

                                    <Box
                                        sx={{
                                            marginLeft: 'auto',
                                            display: 'none',
                                        }}
                                        className="row-icon"
                                    >
                                        {onClickRowIcon && (
                                            <IconButton
                                                id={
                                                    id +
                                                    '-option-' +
                                                    index +
                                                    '-edit'
                                                }
                                                onClick={(e) =>
                                                    onClickRowIconMulti(e, item)
                                                }
                                                aria-controls="simple-menu"
                                                aria-haspopup="true"
                                                key="edit"
                                                disabled={
                                                    itemEditDisable
                                                        ? item[itemEditDisable]
                                                        : false
                                                }
                                                sx={{
                                                    padding: '0px',
                                                    opacity: itemEditDisable
                                                        ? item[itemEditDisable]
                                                            ? '0'
                                                            : '1'
                                                        : '1',
                                                }}
                                            >
                                                <img
                                                    src="/assets/images/icons/edit.svg"
                                                    alt="Edit"
                                                />
                                            </IconButton>
                                        )}
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </>
                ) : (
                    <>
                        <TruncatedText
                            sx={{ opacity: '0.7', fontSize: '14px' }}
                            text={null}
                        >
                            {label}
                            {required ? '*' : ''}
                        </TruncatedText>
                        <Select
                            labelId={labelId}
                            id={id}
                            value={selected}
                            size="small"
                            multiple
                            open={open}
                            onClose={() => setOpen(false)}
                            onOpen={() => setOpen(true)}
                            onChange={handleChange}
                            aria-label={props.ariaLabel}
                            title={title || label}
                            sx={{
                                width: '100%',
                                fontSize: '14px',
                                '&:hover .clear-icon': {
                                    opacity: 1,
                                },
                            }}
                            displayEmpty
                            renderValue={(selected) => {
                                if (selected?.length === 0) {
                                    return (
                                        <TruncatedText
                                            sx={{
                                                fontSize: '14px',
                                                opacity: '0.2',
                                            }}
                                            text={t('selectLabel', { label })}
                                        />
                                    )
                                } else {
                                    return (
                                        <Box width="100%">
                                            <TruncatedText
                                                sx={{ fontSize: '14px' }}
                                                text={
                                                    Array.isArray(selected)
                                                        ? selected
                                                              ?.map((v) => {
                                                                  const item =
                                                                      list.find(
                                                                          (
                                                                              i,
                                                                          ) =>
                                                                              inputProps?.id
                                                                                  ? i[
                                                                                        inputProps
                                                                                            .id
                                                                                    ] ===
                                                                                    v
                                                                                  : i.id ===
                                                                                    v,
                                                                      )

                                                                  // Check if an item is found and retrieve the appropriate property
                                                                  return item
                                                                      ? inputProps?.name
                                                                          ? item[
                                                                                inputProps
                                                                                    .name
                                                                            ]
                                                                          : item.name
                                                                      : ''
                                                              })
                                                              .filter(Boolean) // Filter out empty strings if no item is found
                                                              .join(', ')
                                                        : ''
                                                }
                                            />
                                        </Box>
                                    )
                                }
                            }}
                            input={<OutlinedInput sx={{ fontSize: '14px' }} />}
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        maxHeight: 250,
                                        width: ui_width ? ui_width : '',
                                    },
                                },
                            }}
                            error={errorMessage !== ''}
                            endAdornment={
                                allowClear &&
                                selected?.length > 0 &&
                                !disabled ? (
                                    <IconButton
                                        className="clear-icon"
                                        sx={{
                                            display: 'flex',
                                            position: 'absolute',
                                            right: '25px',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            opacity: 0,
                                            transition: 'opacity 0.2s ease',
                                        }}
                                        onClick={handleClear}
                                        size="small"
                                        id={`${id}-clear-button`}
                                    >
                                        <Clear
                                            sx={{
                                                margin: 'auto',
                                                fontSize: '1.25rem',
                                            }}
                                            id={`${id}-clear-icon`}
                                        />
                                    </IconButton>
                                ) : null
                            }
                        >
                            {hasAddNew && (
                                <MenuItem
                                    // value={value}
                                    sx={{
                                        background: '#fff !important',
                                        paddingLeft: '8px',
                                    }}
                                    className="ghfhgvhjbvhigvygvh"
                                    id={id + '-add-new'}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            width: '100%',
                                        }}
                                        onClick={() => {
                                            setOpen(false)
                                            onClickAddNew()
                                        }}
                                    >
                                        <AddRounded color={'primary'} />
                                        <Box
                                            sx={{
                                                marginLeft: '3px',
                                            }}
                                        >
                                            <Typography
                                                fontWeight="600"
                                                fontSize="14px"
                                            >
                                                Add New
                                            </Typography>
                                        </Box>
                                    </Box>
                                </MenuItem>
                            )}
                            {list.map((item, index) => (
                                <MenuItem
                                    sx={{
                                        fontSize: '14px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        width: ui_width ? ui_width : 'auto',
                                        padding: '7px 15px 7px 10px',
                                        '&:hover': {
                                            '& > .row-icon': {
                                                display: 'flex',
                                            },
                                        },
                                    }}
                                    id={id + '-option-' + index}
                                    key={
                                        inputProps?.id
                                            ? item[inputProps.id]
                                            : item.id
                                    }
                                    value={
                                        inputProps?.id
                                            ? item[inputProps.id]
                                            : item.id
                                    }
                                >
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        width={
                                            onClickRowIcon
                                                ? 'calc(100% - 20px)'
                                                : '100%'
                                        }
                                    >
                                        <Checkbox
                                            id={
                                                id +
                                                '-option-' +
                                                index +
                                                '-checkbox'
                                            }
                                            size="small"
                                            checked={
                                                selected.indexOf(
                                                    inputProps?.id
                                                        ? item[inputProps.id]
                                                        : item.id,
                                                ) > -1
                                            }
                                            sx={{
                                                padding: '0px',
                                                paddingRight: '5px',
                                            }}
                                        />
                                        <TruncatedText
                                            text={
                                                inputProps?.name
                                                    ? item[inputProps.name]
                                                    : item.name
                                            }
                                        />
                                    </Box>

                                    <Box
                                        sx={{
                                            marginLeft: 'auto',
                                            display: 'none',
                                        }}
                                        className="row-icon"
                                    >
                                        {onClickRowIcon && (
                                            <Box>
                                                <IconButton
                                                    id={
                                                        id +
                                                        '-option-' +
                                                        index +
                                                        '-edit'
                                                    }
                                                    onClick={(e) =>
                                                        // onClickRowIcon(item)
                                                        onClickRowIconMulti(
                                                            e,
                                                            item,
                                                        )
                                                    }
                                                    aria-controls="simple-menu"
                                                    aria-haspopup="true"
                                                    key="edit"
                                                    disabled={
                                                        itemEditDisable
                                                            ? item[
                                                                  itemEditDisable
                                                              ]
                                                            : false
                                                    }
                                                    sx={{
                                                        padding: '0px',
                                                        opacity: itemEditDisable
                                                            ? item[
                                                                  itemEditDisable
                                                              ]
                                                                ? '0'
                                                                : '1'
                                                            : '1',
                                                    }}
                                                >
                                                    <img
                                                        src="/assets/images/icons/edit.svg"
                                                        alt="Edit"
                                                    />
                                                </IconButton>
                                            </Box>
                                        )}
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </>
                )}

                {errorMessage !== '' ? (
                    <FormHelperText sx={{ color: 'red' }}>
                        {errorMessage}
                    </FormHelperText>
                ) : (
                    <></>
                )}
            </FormControl>
        </Box>
    )
}

export default MultiSelectInput
